// src/Pages/Hmo/CreatePassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL;

function CreatePassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    
    // ✅ Redirect guard: Ensure Step 1 data exists
    const step1Data = localStorage.getItem('signupStep1');
    if (!step1Data) {
      console.log('🔍 No signupStep1 found, redirecting to /get-started');
      navigate('/get-started', { replace: true });
    }
  }, [navigate]);

  // Password validation rules
  const validationRules = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  const isPasswordValid = Object.values(validationRules).every(rule => rule) && 
                          password === confirmPassword && 
                          password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🚀 Form submitted, validating...');
    
    // ✅ Client-side validation
    if (!isPasswordValid) {
      console.warn('⚠️ Password validation failed');
      setApiError('Please ensure your password meets all requirements and matches confirmation');
      toast.warning('Please ensure your password meets all requirements and matches confirmation');
      return;
    }
    
    setLoading(true);
    setApiError('');
    setApiSuccess('');
    
    try {
      console.log('📦 Preparing API payload...');
      
      // ✅ Get Step 1 data from localStorage
      const step1Data = JSON.parse(localStorage.getItem('signupStep1') || '{}');
      console.log('📋 Step 1 data:', step1Data);
      
      // ✅ Validate AccountType exists
      if (!step1Data.accountType) {
        throw new Error('Account type is missing. Please restart registration.');
      }
      
      // ✅ Format phone number: remove non-digits and prepend country code
      const formattedPhone = `${step1Data.countryCode || '+234'}${step1Data.phone.replace(/\D/g, '')}`;
      console.log('📱 Formatted phone:', formattedPhone);
      
      // ✅ Prepare payload with PascalCase to match C# SendOtpRequest model
      const payload = {
        FirstName: step1Data.firstName?.trim(),
        LastName: step1Data.lastName?.trim(),
        PhoneNumber: formattedPhone,
        Email: step1Data.email?.toLowerCase().trim(),
        Title: step1Data.title,
        HearAbout: step1Data.hearAbout,
        Password: password,
        AccountType: step1Data.accountType  // ✅ "Individual" or "Corporate"
      };

      console.log('📤 Calling authenticatedFetch to /api/HMO/send-otp...');
      console.log('🔍 Payload being sent:', JSON.stringify(payload, null, 2));
      
      // ✅ Call authenticatedFetch - handles auth headers automatically
      const result = await authenticatedFetch(
        `${API_BASE}/HMO/send-otp`,
        'POST',
        payload
      );

      console.log('✅ API Response:', result);

      // ✅ Handle success response
      if (result.status === 'success' || result.success === true) {
        console.log('🎉 OTP sent successfully, preparing redirect...');
        
        const successMessage = result.message || 'OTP sent successfully! Check your email/phone.';
        setApiSuccess(successMessage);
        toast.success(successMessage);
        
        // ✅ Store signup data for next steps (excluding password)
        const { Password: _, ...safeData } = { ...step1Data, password };
        localStorage.setItem('signupComplete', JSON.stringify(safeData));
        localStorage.setItem('otpReference', result.data?.reference || result.reference || '');
        
        console.log('💾 Stored signupComplete and otpReference in localStorage');
        
        // ✅ Navigate to verification page
        navigate('/verify-account', { 
          replace: true,
          state: { fromPasswordPage: true, message: successMessage }
        });
        
        // ✅ Fallback redirect if React Router fails
        setTimeout(() => {
          if (window.location.pathname !== '/verify-account') {
            window.location.replace('/verify-account');
          }
        }, 300);
        
      } else {
        console.error('❌ API returned non-success status:', result);
        throw new Error(result.message || 'Failed to send OTP');
      }
      
    } catch (error) {
      console.error('💥 Send OTP error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      // ✅ Handle specific error messages
      let errorMessage = error.message || 'Failed to send OTP. Please try again.';
      
      if (error.message?.toLowerCase().includes('email already exists')) {
        errorMessage = 'This email is already registered. Please login or use a different email.';
      } else if (error.message?.toLowerCase().includes('phone already exists')) {
        errorMessage = 'This phone number is already registered.';
      } else if (error.message?.includes('Failed to fetch') || error.name === 'TypeError') {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      } else if (error.message?.toLowerCase().includes('accounttype')) {
        errorMessage = 'Invalid account type selected. Please restart registration.';
      }
      
      setApiError(errorMessage);
      toast.error(errorMessage);
      
    } finally {
      console.log('🔄 Resetting loading state');
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    console.log('🔙 Go back button clicked');
    
    // ✅ Prevent going back after OTP is sent (optional security)
    const otpReference = localStorage.getItem('otpReference');
    if (otpReference) {
      console.log('⚠️ OTP already sent, forcing forward to /verify-account');
      navigate('/verify-account', { replace: true });
    } else {
      console.log('✅ Navigating back to /get-started');
      navigate('/get-started');
    }
  };

  return (
    <div className="create-password-page">
      {/* Header */}
      <header className="header-area header-one">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center py-3">
            <a href="/" className="brand-logo">
              <img src="images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
            </a>
            <button 
              className="theme-btn style-two" 
              onClick={() => navigate('/')}
              disabled={loading}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Form Section */}
      <section className="create-password-section py-5" style={{ background: '#fafafa', minHeight: '80vh' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6 col-md-8">
              {/* Progress Indicators */}
              <div className="text-center mb-4" data-aos="fade-down">
                <div className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-success" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                  <span className="d-inline-block rounded-circle bg-light border" style={{ width: '12px', height: '12px' }}></span>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-center mb-4" data-aos="fade-up">Create Password</h2>

              {/* Info Box */}
              <div className="alert alert-info border-0 mb-4" role="alert" data-aos="fade-up" style={{ backgroundColor: '#f3f0ff', color: '#5a4fcf', border: '1px solid #d4ceff' }}>
                <div className="d-flex align-items-start">
                  <i className="fas fa-info-circle me-2 mt-1" style={{ color: '#5a4fcf' }}></i>
                  <small>Make sure your password meets the requirement below.</small>
                </div>
              </div>

              {/* Form Card */}
              <div className="card shadow-sm border-0" data-aos="fade-up" data-aos-delay="200">
                <div className="card-body p-4 p-md-5">
                  <form onSubmit={handleSubmit}>
                    {/* ✅ Inline API Response Messages */}
                    {apiError && (
                      <div className="alert alert-danger border-0 mb-4" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {apiError}
                      </div>
                    )}
                    {apiSuccess && (
                      <div className="alert alert-success border-0 mb-4" role="alert">
                        <i className="fas fa-check-circle me-2"></i>
                        {apiSuccess}
                      </div>
                    )}

                    {/* Password Field */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold small">Password *</label>
                      <div className="position-relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control form-control-lg"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (apiError) setApiError('');
                            if (apiSuccess) setApiSuccess('');
                          }}
                          required
                          disabled={loading}
                          placeholder="Enter a password"
                          style={{ 
                            backgroundColor: '#f8f9fa', 
                            border: apiError ? '2px solid #dc3545' : '1px solid #e9ecef',
                            paddingRight: '50px' 
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute top-50 end-0 translate-middle-y text-muted"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                          style={{ textDecoration: 'none', right: '15px' }}
                        >
                          {showPassword ? (
                            <i className="fas fa-eye-slash" style={{ color: '#6c757d' }}></i>
                          ) : (
                            <i className="fas fa-eye" style={{ color: '#6c757d' }}></i>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="mb-4">
                      <ul className="list-unstyled">
                        <li className={`mb-2 d-flex align-items-center ${validationRules.minLength ? 'text-success' : 'text-muted'}`}>
                          <i className={`fas fa-circle me-2 ${validationRules.minLength ? 'text-success' : ''}`} style={{ fontSize: '6px' }}></i>
                          <small>8 or more characters</small>
                        </li>
                        <li className={`mb-2 d-flex align-items-center ${validationRules.hasUpperCase && validationRules.hasLowerCase ? 'text-success' : 'text-muted'}`}>
                          <i className={`fas fa-circle me-2 ${validationRules.hasUpperCase && validationRules.hasLowerCase ? 'text-success' : ''}`} style={{ fontSize: '6px' }}></i>
                          <small>Upper + lowercase letters</small>
                        </li>
                        <li className={`mb-2 d-flex align-items-center ${validationRules.hasNumber ? 'text-success' : 'text-muted'}`}>
                          <i className={`fas fa-circle me-2 ${validationRules.hasNumber ? 'text-success' : ''}`} style={{ fontSize: '6px' }}></i>
                          <small>At least one number</small>
                        </li>
                        <li className={`mb-2 d-flex align-items-center ${validationRules.hasSpecialChar ? 'text-success' : 'text-muted'}`}>
                          <i className={`fas fa-circle me-2 ${validationRules.hasSpecialChar ? 'text-success' : ''}`} style={{ fontSize: '6px' }}></i>
                          <small>Contains a special character</small>
                        </li>
                      </ul>
                    </div>

                    {/* Confirm Password Field */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold small">Confirm Password *</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-control form-control-lg"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (apiError) setApiError('');
                          if (apiSuccess) setApiSuccess('');
                        }}
                        required
                        disabled={loading}
                        placeholder="Confirm your password"
                        style={{ 
                          backgroundColor: '#f8f9fa', 
                          border: apiError ? '2px solid #dc3545' : '1px solid #e9ecef'
                        }}
                      />
                      {confirmPassword && password !== confirmPassword && (
                        <small className="text-danger mt-1 d-block">Passwords do not match</small>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="d-flex justify-content-between gap-3 mt-5">
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4 py-3"
                        onClick={handleGoBack}
                        disabled={loading}
                        style={{ fontWeight: '600' }}
                      >
                        <i className="fas fa-arrow-left me-2"></i>Go back
                      </button>
                      <button
                        type="submit"
                        className={`theme-btn style-one btn-lg px-5 py-3 ${!isPasswordValid || loading ? 'disabled' : ''}`}
                        disabled={!isPasswordValid || loading}
                        style={{ fontWeight: '600' }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sending OTP...
                          </>
                        ) : (
                          <>
                            Continue <i className="fas fa-arrow-right ms-2"></i>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="py-4 bg-white text-center">
        <div className="container">
          <p className="mb-0 small text-muted">&copy; 2025 FSL HMO. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default CreatePassword;